type QuizSummaryLabelBuilder = (
  value: string,
  params: URLSearchParams,
) => string | null;

export type QuizSummaryPageContext =
  | {
      pageType: "fertilizer";
    }
  | {
      pageType: "bundle";
      productId: string;
    };

type QuizSummaryItemApplicability =
  | { type: "all" }
  | { type: "fertilizer" }
  | { type: "all-bundles" }
  | { type: "bundle-products"; productIds: string[] };

type QuizSummaryItem = {
  key: string;
  getLabel: QuizSummaryLabelBuilder;
  appliesTo: QuizSummaryItemApplicability;
};

const truthyLabel =
  (label: string): QuizSummaryLabelBuilder =>
  (value) =>
    value === "true" ? label : null;

const valueLabel =
  (getLabel: (value: string) => string): QuizSummaryLabelBuilder =>
  (value) =>
    value ? getLabel(value) : null;

const MOOS_FIX_PRODUCT_ID = "15629606879558";
const UNKRAUT_FIX_PRODUCT_ID = "15629606846790";
const UNKRAUT_UND_MOOS_FIX_PRODUCT_ID = "15629606912326";
const LUECKEN_FIX_PRODUCT_ID = "15629606945094";

const ALL_PAGES: QuizSummaryItemApplicability = { type: "all" };
const FERTILIZER_ONLY: QuizSummaryItemApplicability = { type: "fertilizer" };
const ALL_BUNDLES: QuizSummaryItemApplicability = { type: "all-bundles" };
const WEEDS_BUNDLES: QuizSummaryItemApplicability = {
  type: "bundle-products",
  productIds: [UNKRAUT_FIX_PRODUCT_ID, UNKRAUT_UND_MOOS_FIX_PRODUCT_ID],
};
const MOSS_BUNDLES: QuizSummaryItemApplicability = {
  type: "bundle-products",
  productIds: [MOOS_FIX_PRODUCT_ID, UNKRAUT_UND_MOOS_FIX_PRODUCT_ID],
};
const LUECKEN_FIX_ONLY: QuizSummaryItemApplicability = {
  type: "bundle-products",
  productIds: [LUECKEN_FIX_PRODUCT_ID],
};

const normalizeProductId = (productId: string) =>
  String(productId).split("/").pop() ?? productId;

export const isQuizSummaryItemApplicable = (
  item: QuizSummaryItem,
  pageContext: QuizSummaryPageContext,
) => {
  if (item.appliesTo.type === "all") return true;
  if (item.appliesTo.type === "fertilizer") {
    return pageContext.pageType === "fertilizer";
  }
  if (item.appliesTo.type === "all-bundles") {
    return pageContext.pageType === "bundle";
  }
  if (pageContext.pageType !== "bundle") return false;

  return item.appliesTo.productIds.includes(
    normalizeProductId(pageContext.productId),
  );
};

export const QUIZ_SUMMARY_ITEMS: QuizSummaryItem[] = [
  {
    key: "lawn_size",
    getLabel: valueLabel((value) => `${value} m²`),
    appliesTo: ALL_PAGES,
  },
  {
    key: "pdp_zip",
    getLabel: valueLabel((value) => value),
    appliesTo: FERTILIZER_ONLY,
  },
  {
    key: "organic",
    getLabel: truthyLabel("Bio-Dünger"),
    appliesTo: FERTILIZER_ONLY,
  },
  {
    key: "usage__nobody",
    getLabel: truthyLabel("Zierrasen"),
    appliesTo: FERTILIZER_ONLY,
  },
  {
    key: "minimal_effort",
    getLabel: truthyLabel("Minimaler Aufwand"),
    appliesTo: FERTILIZER_ONLY,
  },
  {
    key: "quick_results",
    getLabel: truthyLabel("schnelle Verbesserungen"),
    appliesTo: FERTILIZER_ONLY,
  },
  {
    key: "healthy_lawn",
    getLabel: truthyLabel("Rasengesundheit"),
    appliesTo: FERTILIZER_ONLY,
  },
  {
    key: "safe_care",
    getLabel: truthyLabel("Nachhaltige Pflege"),
    appliesTo: FERTILIZER_ONLY,
  },
  {
    key: "kids",
    getLabel: truthyLabel("Kinder"),
    appliesTo: ALL_PAGES,
  },
  {
    key: "pets",
    getLabel: truthyLabel("Haustiere"),
    appliesTo: ALL_PAGES,
  },
  {
    key: "weeds",
    getLabel: truthyLabel("Unkraut"),
    appliesTo: WEEDS_BUNDLES,
  },
  {
    key: "moss",
    getLabel: truthyLabel("Moos"),
    appliesTo: MOSS_BUNDLES,
  },
  {
    key: "bare_spots",
    getLabel: truthyLabel("Kahle Stellen"),
    appliesTo: ALL_BUNDLES,
  },
  {
    key: "low_density",
    getLabel: truthyLabel("Geringe Halmdichte"),
    appliesTo: ALL_BUNDLES,
  },
  {
    key: "yellow_spots",
    getLabel: truthyLabel("gelb/Hitzestress"),
    appliesTo: LUECKEN_FIX_ONLY,
  },
  {
    key: "urine_spots",
    getLabel: truthyLabel("Urinflecken"),
    appliesTo: LUECKEN_FIX_ONLY,
  },
];
